const API_URL = (import.meta.env.VITE_API_URL ?? "").replace(/\/$/, "");

// In dev, apiFetch pins requests to a seeded tenant so browsing via
// localhost works. But the pin must not apply when the app is opened
// under a real hostname (e.g. live.u2tour.de mapped in /etc/hosts):
// otherwise the product host would also resolve that tenant and the
// landing-page fallback could never be reached locally.
const isLoopbackHostname =
  ["localhost", "::1", "[::1]"].includes(window.location.hostname) ||
  window.location.hostname.endsWith(".local") ||
  /^\d{1,3}(\.\d{1,3}){3}$/.test(window.location.hostname);

const DEV_TENANT =
  import.meta.env.VITE_DEV_TENANT_HOST ??
  (isLoopbackHostname ? "localhost" : "");

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message);
  }
}

export async function apiFetch<T>(
  path: string,
  options: RequestInit & { raw?: false } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (
    !(options.body instanceof FormData) &&
    options.body &&
    !headers.has("Content-Type")
  ) {
    headers.set("Content-Type", "application/json");
  }
  if (import.meta.env.DEV && DEV_TENANT) {
    headers.set("x-vz-tenant", DEV_TENANT);
  }

  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers,
    credentials: "include",
  });
  if (!response.ok) {
    const result = (await response.json().catch(() => null)) as {
      error?: string;
      code?: string;
      details?: unknown;
    } | null;
    throw new ApiError(
      result?.error ?? `Request failed with ${response.status}`,
      response.status,
      result?.code,
      result?.details,
    );
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export const apiUrl = (path: string) => {
  const url = new URL(`${API_URL}${path}`, window.location.origin);
  if (import.meta.env.DEV && DEV_TENANT) {
    url.searchParams.set("tenant", DEV_TENANT);
  }
  return url.toString();
};

export const tenantHeaders = () =>
  import.meta.env.DEV && DEV_TENANT ? { "x-vz-tenant": DEV_TENANT } : {};
